import React, { useState } from 'react';
import { debounce } from 'throttle-debounce';
import PropTypes from 'prop-types';

import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import DateRangePicker from '@welldev-group/rcl-date-range-picker';
import SelectBox from '@welldev-group/rcl-select-box';
import IconButton from '@welldev-group/rcl-icon-button';
import { I18nText } from '@welldev-group/react-comp-lib';

import SupportTicketCard from './SupportTicketCard';
import SupportTicketModal from './SupportTicketModal';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import selectBoxObjectMapper from '../../../shared/helper/selectBoxObjectMapper';

const LeftPanel = props => {
  const {
    fetchTickets,
    signedInAsSuperAdmin,
    supportTickets,
    selectedTicketId,
    setSelectedTicketId,
    searchParam,
    setSearchParam,
    affiliateId,
    setAffiliateId,
    superAdmins,
    assigneeId,
    setAssigneeId,
    statuses,
    setStatuses,
    leftPanelShow,
    setLeftPanelShow,
    setStartDate,
    setEndDate,
  } = props;

  const [isFiltering, setIsFiltering] = useState(false);
  const [affiliateOptions, setAffiliateOptions] = useState([]);

  const assigneeList = superAdmins.map(superAdmin => ({
    label: `${superAdmin.first_name} ${superAdmin.last_name}`,
    value: superAdmin.id,
  }));

  const changeSearchParam = value => {
    setSearchParam(value);
  };

  const fetchAffiliateOptions = debounce(1000, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.affiliates.list({ q: inputValue }),
      );
      setAffiliateOptions(
        data.affiliates.map(affiliate => ({
          value: affiliate.id,
          label: affiliate.clientName,
        })),
      );
    }
  });

  const onOrganizationChange = selectedOption => {
    setAffiliateId(selectedOption && selectedOption.value);
  };

  const onAssigneeChange = selectedOption => {
    setAssigneeId(selectedOption && selectedOption.value);
  };

  const onStatusesChange = selectedOption => {
    setStatuses(selectedOption && selectedOption.map(status => status.value));
  };

  const onDateRangeChange = ({ startDate, newEndDate }) => {
    if (startDate && newEndDate) {
      setStartDate(startDate.format('YYYY-MM-DD'));
      setEndDate(newEndDate.format('YYYY-MM-DD'));
    }
  };

  const statusOptions = signedInAsSuperAdmin
    ? [
        { label: <I18nText id="support.ticket.state.open" />, value: 'open' },
        { label: <I18nText id="support.ticket.state.ready" />, value: 'ready' },
        {
          label: <I18nText id="support.ticket.state.submitted" />,
          value: 'submitted',
        },
        {
          label: <I18nText id="support.ticket.state.resolved" />,
          value: 'resolved',
        },
      ]
    : [
        { label: <I18nText id="support.ticket.state.open" />, value: 'open' },
        {
          label: <I18nText id="support.ticket.state.resolved" />,
          value: 'resolved',
        },
      ];

  const currentStatusValue = statusOptions.map(
    item => statuses?.includes(item.value) && item,
  );

  return (
    <div className="support-left-panel">
      <div className="support-left-panel__actions">
        <div
          onClick={() => setLeftPanelShow(!leftPanelShow)}
          role="presentation"
        >
          <I18nText id="support.back" />
        </div>
        <IconButton
          version="v2"
          icon={<Icon name="arrowBack" />}
          onClick={() => setLeftPanelShow(!leftPanelShow)}
          size="tiny"
          isIconOnly={true}
        />
      </div>
      <div className="support-left-panel__header">
        <I18nText id="support.page_title" />
        <SupportTicketModal
          fetchTickets={fetchTickets}
          signedInAsSuperAdmin={signedInAsSuperAdmin}
        />
      </div>
      <div className="support-left-panel__search-filter">
        <div className="grid support-left-panel__search-filter__container">
          <div className="col-10 col-xlg-10 col-lg-9 col-md-9 col-sm-11 col-bleed-x support-left-panel__search-box">
            <Input
              value={searchParam}
              onChange={changeSearchParam}
              preIcon={<Icon name="search" />}
              placeholder={I18n.t('components.support.search')}
            />
          </div>
          <div className="col-2 col-xlg-2 col-lg-3 col-md-3 col-sm-1 col-grid direction-row col-bleed-x support-left-panel__filter-button">
            <div className="col-12 col-grid align-end col-bleed support-left-panel__filter-button-container">
              <IconButton
                icon={<Icon name="filter" />}
                onClick={() => setIsFiltering(!isFiltering)}
                size="small"
                label={<I18nText id="support.filter" />}
              />
            </div>
          </div>
        </div>
      </div>
      {isFiltering && (
        <div className="support-left-panel__search-filter-section">
          <div className="support-left-panel__search-filter-options">
            <DateRangePicker
              onChange={onDateRangeChange}
              canSelectPastDates={true}
              fullWidth={true}
            />
          </div>
          <div className="support-left-panel__search-filter-options">
            <SelectBox
              value={currentStatusValue}
              onChange={onStatusesChange}
              options={statusOptions}
              placeholderText={<I18nText id="support.status" />}
              isMulti={true}
              required={false}
              width="full"
            />
          </div>
          {signedInAsSuperAdmin && (
            <div className="support-left-panel__search-filter-options">
              <SelectBox
                value={selectBoxObjectMapper(affiliateOptions, affiliateId)}
                onChange={onOrganizationChange}
                onInputChange={fetchAffiliateOptions}
                options={affiliateOptions}
                placeholderText={<I18nText id="support.organization" />}
                required={false}
                width="full"
              />
            </div>
          )}
          {signedInAsSuperAdmin && (
            <div className="support-left-panel__search-filter-options">
              <SelectBox
                value={selectBoxObjectMapper(assigneeList, assigneeId)}
                onChange={onAssigneeChange}
                options={assigneeList}
                placeholderText={<I18nText id="support.assignee" />}
                required={false}
                width="full"
              />
            </div>
          )}
        </div>
      )}
      <div className="support-left-panel__support-list">
        {supportTickets.map(supportTicket => (
          <div
            className="support-left-panel__support-list-item"
            key={supportTicket.id}
            onClick={() => leftPanelShow && setLeftPanelShow(!leftPanelShow)}
            role="presentation"
          >
            <SupportTicketCard
              supportTicket={supportTicket}
              selectedTicketId={selectedTicketId}
              setSelectedTicketId={setSelectedTicketId}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

LeftPanel.defaultProps = {
  selectedTicketId: null,
  affiliateId: null,
  assigneeId: null,
};

LeftPanel.propTypes = {
  fetchTickets: PropTypes.func.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  supportTickets: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selectedTicketId: PropTypes.number,
  setSelectedTicketId: PropTypes.func.isRequired,
  searchParam: PropTypes.string.isRequired,
  setSearchParam: PropTypes.func.isRequired,
  affiliateId: PropTypes.number,
  setAffiliateId: PropTypes.func.isRequired,
  superAdmins: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  assigneeId: PropTypes.number,
  setAssigneeId: PropTypes.func.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  setStatuses: PropTypes.func.isRequired,
  leftPanelShow: PropTypes.bool.isRequired,
  setLeftPanelShow: PropTypes.func.isRequired,
  setStartDate: PropTypes.func.isRequired,
  setEndDate: PropTypes.func.isRequired,
};

export default LeftPanel;
